import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import PhoneIcon from '@material-ui/icons/Phone';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import CallMadeIcon from '@material-ui/icons/CallMade';
import CallReceivedIcon from '@material-ui/icons/CallReceived';
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import EmptyState from "../../components/EmptyState";
import { PageHeader, StatusBadge } from "../../components/ui";
import { AdminMetrics } from "../../components/AdminWorkspace";
import { usePanelStyles, FilterBar, SearchField, TableCard, RowActions } from "../../components/FormFields/panel";
import { getTokens } from "../../styles/tokens";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CALL_HISTORY") {
    const callHistory = action.payload;
    const newCallHistory = [];
    if (isArray(callHistory)) {
      callHistory.forEach((call) => {
        const callIndex = state.findIndex(
          (c) => c.id === call.id
        );
        if (callIndex !== -1) {
          state[callIndex] = call;
        } else {
          newCallHistory.push(call);
        }
      });
    }
    return [...state, ...newCallHistory];
  }
  if (action.type === "UPDATE_CALL_HISTORY") {
    const call = action.payload;
    const callIndex = state.findIndex((c) => c.id === call.id);
    if (callIndex !== -1) {
      state[callIndex] = call;
      return [...state];
    } else {
      return [call, ...state];
    }
  }
  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    nowrap: { whiteSpace: "nowrap" },
    direction: { display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" },
    directionIcon: { fontSize: 16, color: t.textMuted },
  };
});

const CallHistoricals = () => {
  const classes = useStyles();
  const p = usePanelStyles();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [callHistory, dispatch] = useReducer(reducer, []);
  const [statistics, setStatistics] = useState({
    total: 0,
    totalReject: 0,
    totalServed: 0,
    totalFinish: 0
  });
  const { user, socket } = useContext(AuthContext);
  const { profile } = user;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCallHistory();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onCallHistoryEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CALL_HISTORY", payload: data.record });
      }
    };
    
    if (socket) {
      socket.on(`company-${companyId}-call-history`, onCallHistoryEvent);
    }
    return () => {
      if (socket) {
        socket.off(`company-${companyId}-call-history`, onCallHistoryEvent);
      }
    };
  }, [socket, user.companyId]);

  const fetchCallHistory = async () => {
    try {
      const { data } = await api.get("/call/historical", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CALL_HISTORY", payload: data.historical.resultFinal });
      setStatistics({
        total: data.historical.total || 0,
        totalReject: data.historical.totalReject || 0,
        totalServed: data.historical.totalServed || 0,
        totalFinish: data.historical.totalFinish || 0
      });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleMakeCall = (callUrl) => {
    window.open(callUrl, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhone = (phone) => {
    // Formato brasileiro: +55 (11) 99999-9999
    if (phone && phone.length >= 10) {
      const cleaned = phone.replace(/\D/g, '');
      if (cleaned.startsWith('55')) {
        const ddd = cleaned.substring(2, 4);
        const number = cleaned.substring(4);
        if (number.length === 9) {
          return `+55 (${ddd}) ${number.substring(0, 5)}-${number.substring(5)}`;
        } else if (number.length === 8) {
          return `+55 (${ddd}) ${number.substring(0, 4)}-${number.substring(4)}`;
        }
      }
    }
    return phone;
  };

  // tom do selo de status (mesma classificação de antes: neutro, ativo, atenção, erro)
  const getStatusTone = (status) => {
    switch (status) {
      case 'ENDED':
        return "neutral";
      case 'ACTIVE':
        return "brand";
      case 'RINGING':
        return "warning";
      case 'BUSY':
      case 'NO_ANSWER':
        return status === 'NO_ANSWER'
          ? "warning"
          : "error";
      case 'REJECTED':
        return "error";
      default:
        return "neutral";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ENDED':
        return 'Finalizada';
      case 'ACTIVE':
        return 'Ativa';
      case 'RINGING':
        return 'Tocando';
      case 'BUSY':
        return 'Ocupado';
      case 'NO_ANSWER':
        return 'Não Atendida';
      case 'REJECTED':
        return 'Rejeitada';
      default:
        return status || 'N/A';
    }
  };

  const getDirectionIcon = (direction) => {
    if (direction === 'OUTCOMING') {
      return <CallMadeIcon className={classes.directionIcon} />;
    } else if (direction === 'INCOMING') {
      return <CallReceivedIcon className={classes.directionIcon} />;
    }
    return null;
  };

  const getDirectionLabel = (direction) => {
    switch (direction) {
      case 'OUTCOMING':
        return 'Saída';
      case 'INCOMING':
        return 'Entrada';
      default:
        return direction || 'N/A';
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer maxWidth>
      <PageHeader
        title="Histórico de ligações"
        subtitle="Ligações feitas e recebidas, com status, duração e gravação."
      />
      <AdminMetrics
        items={[
          { value: statistics.total, label: "Total de ligações" },
          { value: statistics.totalServed, label: "Ligações atendidas" },
          { value: statistics.totalReject, label: "Ligações rejeitadas" },
          { value: statistics.totalFinish, label: "Ligações finalizadas" },
        ]}
      />
      <FilterBar>
        <SearchField
          value={searchParam}
          onChange={handleSearch}
          placeholder="Pesquisar por nome ou telefone..."
        />
      </FilterBar>
      <TableCard fill onScroll={handleScroll}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Atendente</TableCell>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Direção</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Duração</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {callHistory.map((call) => (
                <TableRow key={call.id || call.devices?.id}>
                  <TableCell className={p.cellStrong}>
                    {call.name || (call.devices?.direction === 'INCOMING' ? 'Ligação Recebida' : 'N/A')}
                  </TableCell>
                  <TableCell className={classes.nowrap}>
                    {formatPhone(call.phone_to)}
                  </TableCell>
                  <TableCell>
                    {call.user?.name || 'N/A'}
                  </TableCell>
                  <TableCell className={`${classes.nowrap} ${p.cellMuted}`}>
                    {formatDate(call.createdAt || call.devices?.created_date)}
                  </TableCell>
                  <TableCell>
                    <span className={classes.direction}>
                      {getDirectionIcon(call.devices?.direction)}
                      {getDirectionLabel(call.devices?.direction)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      color={getStatusTone(call.devices?.status)}
                      label={getStatusLabel(call.devices?.status)}
                    />
                  </TableCell>
                  <TableCell className={classes.nowrap}>
                    {formatDuration(call.devices?.duration)}
                  </TableCell>
                  <TableCell className={p.cellMuted}>
                    {call.devices?.type || 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <RowActions>
                      {call.callSaveUrl && (
                        <Tooltip title="Baixar gravação">
                          <IconButton
                            size="small"
                            aria-label="Baixar gravação"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = call.callSaveUrl;
                              link.setAttribute('download', 'audio.mp3');
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {call.url && (
                        <Tooltip title="Fazer ligação">
                          <IconButton
                            size="small"
                            aria-label="Fazer ligação"
                            onClick={() => handleMakeCall(call.url)}
                          >
                            <PhoneIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={9} />}
              {!loading && callHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <EmptyState
                      icon={<PhoneIcon />}
                      title="Nenhum registro encontrado"
                      message={searchParam ? "Tente outro nome ou telefone." : "As ligações aparecem aqui assim que forem feitas ou recebidas."}
                    />
                  </TableCell>
                </TableRow>
              )}
            </>
          </TableBody>
        </Table>
      </TableCard>
    </MainContainer>
  );
};

export default CallHistoricals;
